import { useEffect, useRef } from 'react';

import { useStore, useStoreApi } from '@xyflow/react';

import { FlowsInput, FlowsOutput } from 'hooks/data/datasets/V4/flows/types';

type Params = {
  inputs: (FlowsInput | FlowsOutput)[];
  outputs: (FlowsInput | FlowsOutput)[];
};

/**
 * Centers the viewport on the dataset node after it has been measured.
 *
 * Uses panZoom.setViewport directly rather than fitView because in controlled
 * mode (no onNodesChange), React Flow's nodesInitialized never becomes true —
 * it checks userNode.measured on the nodes prop, which we never update with
 * measurements since we have no onNodesChange handler. fitView's internal
 * resolveFitView is gated on nodesInitialized, so it never fires.
 *
 * Instead, we subscribe to nodeLookup directly: when the dataset node gains
 * measured dimensions (set by React Flow's ResizeObserver), we compute the
 * viewport that centers that node and apply it via panZoom.setViewport.
 *
 * The layout key prevents re-centering when the same dataset is re-rendered
 * (user panning/zooming). When a new dataset loads, useMemo recreates all
 * node objects, adoptUserNodes clears nodeLookup measurements, so
 * isDatasetMeasured cycles false→true and we re-center correctly.
 */
export const useAutoLayout = ({ inputs, outputs }: Params) => {
  const storeApi = useStoreApi();
  const isDatasetMeasured = useStore((s) => {
    const node = s.nodeLookup?.get('dataset');
    return !!(node?.measured?.width && node?.measured?.height);
  });
  const layoutKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isDatasetMeasured) return;

    const key = [...inputs, ...outputs].map((f) => f.id).join(',');
    if (key === layoutKeyRef.current) return;

    const timerId = setTimeout(() => {
      const currentKey = [...inputs, ...outputs].map((f) => f.id).join(',');
      if (currentKey === layoutKeyRef.current) return;
      layoutKeyRef.current = currentKey;

      const { panZoom, nodeLookup, domNode } = storeApi.getState();
      if (!panZoom || !domNode || !nodeLookup) return;

      const datasetNode = nodeLookup.get('dataset');
      if (!datasetNode?.measured?.width || !datasetNode?.measured?.height) return;

      const containerWidth = domNode.offsetWidth;
      const containerHeight = domNode.offsetHeight;
      if (!containerWidth || !containerHeight) return;

      const { positionAbsolute } = datasetNode.internals;
      const { width, height } = datasetNode.measured;

      const zoom = 1;
      const x = containerWidth / 2 - (positionAbsolute.x + width / 2) * zoom;
      const y = containerHeight / 2 - (positionAbsolute.y + height / 2) * zoom;

      panZoom.setViewport({ x, y, zoom }, { duration: 600 });
    }, 0);

    return () => clearTimeout(timerId);
  }, [isDatasetMeasured, storeApi, inputs, outputs]);
};
